import type { Employee, WorkSchedule } from '../types/employee';
import { isBusinessServicePay, isDailyWorkerPay } from '../types/employee';
import { payTypeChipLabel } from '../lib/payroll/display-helper';

const UNASSIGNED_LABEL = '미할당';

export function getSnapshotEmployeeName(
  schedule: Pick<WorkSchedule, 'snapshot_employee_name' | 'snapshotEmployeeName'>,
): string | null {
  const name = schedule.snapshot_employee_name ?? schedule.snapshotEmployeeName;
  if (typeof name === 'string' && name.trim().length > 0) return name.trim();
  return null;
}

/** Flutter `WorkSchedule.displayEmployeeName` 1:1 */
export function displayEmployeeName(
  schedule: Pick<
    WorkSchedule,
    'employee_id' | 'employee_name' | 'snapshot_employee_name' | 'snapshotEmployeeName' | 'workTime1' | 'workTime2'
  >,
  employees?: Employee[],
): string {
  if (schedule.employee_id) {
    const linked = employees?.find((e) => e.id === schedule.employee_id);
    if (linked?.name) return linked.name;
    if (schedule.employee_name?.trim()) return schedule.employee_name.trim();
  }

  const snapshot = getSnapshotEmployeeName(schedule);
  if (snapshot) return snapshot;

  return UNASSIGNED_LABEL;
}

/** 퇴직자 삭제(근무 기록 유지) 후 orphan 스케줄 */
export function isOrphanWorkSchedule(
  schedule: Pick<WorkSchedule, 'employee_id' | 'snapshot_employee_name' | 'snapshotEmployeeName'>,
): boolean {
  return !schedule.employee_id && !!getSnapshotEmployeeName(schedule);
}

/** 캘린더 칩 라벨 — 연결 직원 payType 접미사 포함, orphan은 이름만 */
export function scheduleChipLabel(schedule: WorkSchedule, employees: Employee[]): string {
  const name = displayEmployeeName(schedule, employees);
  const employee = schedule.employee_id
    ? employees.find((e) => e.id === schedule.employee_id)
    : undefined;
  if (employee && (isDailyWorkerPay(employee) || isBusinessServicePay(employee))) {
    return `${name}·${payTypeChipLabel(employee)}`;
  }
  return name;
}

/** 급여 집계 대상 — 재직 + 해당 월 스케줄 있는 퇴직자 (Flutter `_employeesForMonthlyPayroll`) */
export function employeesForMonthlyPayroll(
  employees: Employee[],
  schedulesInMonth: WorkSchedule[],
): Employee[] {
  const employeeIdsWithSchedules = new Set(
    schedulesInMonth.map((s) => s.employee_id).filter(Boolean),
  );

  const result: Employee[] = [];
  const seen = new Set<string>();

  for (const employee of employees) {
    if (seen.has(employee.id)) continue;

    const include =
      !employee.isRetired || employeeIdsWithSchedules.has(employee.id);

    if (include) {
      result.push(employee);
      seen.add(employee.id);
    }
  }

  return result;
}
